import React, { useEffect } from 'react';

import {
  type EmailSchedule,
  WINDOW_END_MIN,
  WINDOW_START_MIN,
  formatMinutes,
} from './scheduleTypes';
import styles from './RemoveScheduleModal.module.css';

export type RemoveScheduleState =
  | { phase: 'loading' }
  | { phase: 'empty' }
  | { phase: 'loaded'; schedule: EmailSchedule; deleting: boolean; deleteError: string | null }
  | { phase: 'removed' }
  | { phase: 'loadError'; message: string };

export interface RemoveScheduleModalProps {
  open: boolean;
  state: RemoveScheduleState;
  onClose: () => void;
  onRemove: () => void;
  onRetry: () => void;
}

const describeSchedule = (schedule: EmailSchedule): string => {
  if (schedule.schedule.kind === 'clocked') {
    const labels = schedule.schedule.times_minutes.map(formatMinutes).join(', ');
    return `Clocked at: ${labels}`;
  }
  return (
    `Every ${schedule.schedule.every_minutes} minutes between ` +
    `${formatMinutes(WINDOW_START_MIN)} and ${formatMinutes(WINDOW_END_MIN)} ` +
    `(${schedule.window.tz})`
  );
};

export const RemoveScheduleModal: React.FC<RemoveScheduleModalProps> = ({
  open,
  state,
  onClose,
  onRemove,
  onRetry,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div
      className={styles.backdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
      data-testid="remove-schedule-modal"
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-schedule-modal-title"
      >
        <h2 id="remove-schedule-modal-title" className={styles.title}>
          Remove dashboard schedule
        </h2>
        <Body state={state} />
        <Actions state={state} onClose={onClose} onRemove={onRemove} onRetry={onRetry} />
      </div>
    </div>
  );
};

const Body: React.FC<{ state: RemoveScheduleState }> = ({ state }) => {
  switch (state.phase) {
    case 'loading':
      return (
        <p className={styles.bodyDim} data-testid="remove-schedule-loading">
          Loading schedule…
        </p>
      );
    case 'empty':
      return (
        <p className={styles.bodyDim} data-testid="remove-schedule-empty">
          No schedule attached to this dashboard.
        </p>
      );
    case 'loaded':
      return (
        <div data-testid="remove-schedule-summary">
          <SummaryRow label="Subject" value={state.schedule.subject} />
          <SummaryRow label="To" value={state.schedule.recipient} />
          <SummaryRow label="Schedule" value={describeSchedule(state.schedule)} />
          {state.deleteError ? (
            <p className={styles.formError} data-testid="remove-schedule-error">
              {state.deleteError}
            </p>
          ) : null}
        </div>
      );
    case 'removed':
      return (
        <div className={styles.successBanner} data-testid="remove-schedule-success">
          Schedule removed.
        </div>
      );
    case 'loadError':
      return (
        <p className={styles.formError} data-testid="remove-schedule-load-error">
          {state.message}
        </p>
      );
  }
};

const SummaryRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className={styles.row}>
    <span className={styles.rowLabel}>{label}</span>
    <span className={styles.rowValue}>{value}</span>
  </div>
);

interface ActionsProps {
  state: RemoveScheduleState;
  onClose: () => void;
  onRemove: () => void;
  onRetry: () => void;
}

const Actions: React.FC<ActionsProps> = ({ state, onClose, onRemove, onRetry }) => {
  if (state.phase === 'loaded') {
    return (
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={onClose}
          disabled={state.deleting}
          data-testid="remove-schedule-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          className={styles.dangerButton}
          onClick={onRemove}
          disabled={state.deleting}
          data-testid="remove-schedule-confirm"
        >
          {state.deleting ? 'Removing…' : 'Remove schedule'}
        </button>
      </div>
    );
  }
  if (state.phase === 'loadError') {
    return (
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={onClose}
          data-testid="remove-schedule-close"
        >
          Close
        </button>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={onRetry}
          data-testid="remove-schedule-retry"
        >
          Try again
        </button>
      </div>
    );
  }
  return (
    <div className={styles.actions}>
      <button
        type="button"
        className={styles.primaryButton}
        onClick={onClose}
        data-testid="remove-schedule-close"
      >
        Close
      </button>
    </div>
  );
};
